/**
 * authoxi's binding to the shared lead gate.
 *
 * THE IMPLEMENTATION MOVED. It lives in `cortex/standards/web-kit/leadgate.tsx`, vendored here as
 * `leadgate.tsx` — because there were two of these, one per product, and two implementations of
 * "ask for an email" is two places for the gate rule to be almost right. This file is what is
 * left: the transport, the product name, and the copy defaults. Everything a product genuinely
 * owns, and nothing it does not.
 *
 * The rule the kit enforces is the one this file used to state and now only inherits: **zero
 * asks before a result, exactly one after, one field, for something whose value the visitor has
 * just personally seen.** Gating the first thing costs an order of magnitude in reach.
 */

import { LeadInline, LeadModal, useLeadModal, type LeadCopy, type LeadTransport } from "./leadgate";
import { Client } from "./api";

export type { LeadCopy };
export { useLeadModal, LeadModal };

/** The portfolio taxonomy value for this product. Closed set, enforced server-side. */
const PRODUCT = "authoxi";

/**
 * The leads plane, wrapped so the kit does not have to know this product's client.
 *
 * `submit` is deliberately the only method: a transport that could also read would be a
 * transport somebody uses to read, and the leads table is every address anyone ever left us.
 */
export function leadTransport(client: Client, path = "/leads"): LeadTransport {
  return {
    submit: (body) => client.post<{ unsubscribe_url?: string }>(path, body),
  };
}

export interface GateProps {
  /** The tool this gate is on — becomes `source` on the lead. */
  source: string;
  title: string;
  body: string;
  cta: string;
  /** What the visitor was looking at, e.g. the host they just graded. Named `subject` rather than
   *  `ref` because React reserves `ref`: passing one to a function component makes React try to
   *  attach it, and the page dies with a minified invariant. */
  subject?: string;
  client: Client;
  /** Optional: post to a different route (the grader's watch is scan-scoped). */
  submit?: (email: string) => Promise<{ unsubscribe_url?: string }>;
}

/**
 * The post-result ask. Same name and same props it has always had, so no call site changed.
 *
 * `submit` is still honoured for the one caller that needs a scan-scoped route, and it is adapted
 * rather than special-cased in the kit — a kit parameter for one product's exception is how a
 * shared component starts growing per-product branches.
 */
export function Gate({ source, title, body, cta, subject, client, submit }: GateProps) {
  const transport: LeadTransport = submit
    ? { submit: ({ email }) => submit(email) }
    : leadTransport(client);

  return (
    <LeadInline
      transport={transport}
      product={PRODUCT}
      source={source}
      subject={subject}
      copy={{ title, body, cta }}
    />
  );
}
